import { gsap } from 'gsap';

export default class ScrollRail {
  constructor(options = {}) {
    this.options = {
      minThumb: 40,
      hideDelay: 1.2,
      splitStrength: 0.4,
      maxSplit: 6,
      sectionSelector: 'section[id]',
      ...options,
    };

    // Seulement avec une vraie souris
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches)
      return;

    this.reduceMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches;
    this.state = { split: 0 };
    this.lastScroll = window.scrollY;
    this.dragging = false;
    this.dragStartY = 0;
    this.dragStartScroll = 0;

    this.sections = [
      ...document.querySelectorAll(this.options.sectionSelector),
    ];
    this.tops = [];
    this.ticks = [];
    this.current = -1;

    this.update = this.update.bind(this);
    this.onLayout = this.onLayout.bind(this);
    this.render = this.render.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.onThumbDown = this.onThumbDown.bind(this);
    this.onTrackDown = this.onTrackDown.bind(this);
    this.onDragMove = this.onDragMove.bind(this);
    this.onDragEnd = this.onDragEnd.bind(this);
    this.onTickDown = (e) => e.stopPropagation(); // évite le saut du clic sur le rail

    this.build();
    this.bindEvents();
    this.onLayout();
  }

  build() {
    this.element = document.createElement('div');
    this.element.className = 'scrollrail';
    this.element.innerHTML = `
      <div class="scrollrail__track">
        <div class="scrollrail__thumb">
          <span class="scrollrail__percent">000%</span>
        </div>
      </div>`;
    document.body.appendChild(this.element);

    this.track = this.element.querySelector('.scrollrail__track');
    this.thumb = this.element.querySelector('.scrollrail__thumb');
    this.percent = this.element.querySelector('.scrollrail__percent');
    this.setY = gsap.quickTo(this.thumb, 'y', {
      duration: 0.25,
      ease: 'power3.out',
    });

    // Un cran par section
    this.sections.forEach((section, i) => {
      const title = section.dataset.title || section.id.replace(/[-_]+/g, ' ');

      const tick = document.createElement('button');
      tick.type = 'button';
      tick.className = 'scrollrail__tick';
      tick.setAttribute('aria-label', title);

      const label = document.createElement('span');
      label.className = 'scrollrail__label';
      label.textContent = `${String(i + 1).padStart(2, '0')} · ${title}`;
      tick.appendChild(label);

      tick.addEventListener('pointerdown', this.onTickDown);
      tick.addEventListener('click', () => this.goTo(i));

      this.track.insertBefore(tick, this.thumb);
      this.ticks.push(tick);
    });

    document.documentElement.classList.add('has-scrollrail');
  }

  bindEvents() {
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onLayout);
    this.thumb.addEventListener('pointerdown', this.onThumbDown);
    this.track.addEventListener('pointerdown', this.onTrackDown);

    // Remesure si la hauteur de la page change (images, contenu dynamique)
    this.ro = new ResizeObserver(this.onLayout);
    this.ro.observe(document.documentElement);
  }

  /* ---------- Mesure et placement ---------- */

  metrics() {
    const docH = document.documentElement.scrollHeight;
    const vh = window.innerHeight;
    const maxScroll = Math.max(docH - vh, 0);
    const trackH = this.track.clientHeight;
    const thumbH = Math.max(this.options.minThumb, trackH * (vh / docH));
    return { maxScroll, trackH, thumbH, travel: trackH - thumbH };
  }

  onLayout() {
    // Position de chaque section dans la page
    this.tops = this.sections.map(
      (s) => s.getBoundingClientRect().top + window.scrollY,
    );

    // On affiche le rail avant de mesurer, puis on le cache si la page ne scrolle pas
    this.element.style.display = '';
    const { maxScroll, thumbH, travel } = this.metrics();
    this.element.style.display = maxScroll <= 1 ? 'none' : '';

    this.thumb.style.height = `${thumbH}px`;

    // Les crans sont alignés sur le centre du curseur quand on est sur la section
    this.ticks.forEach((tick, i) => {
      const p = maxScroll
        ? gsap.utils.clamp(0, 1, this.tops[i] / maxScroll)
        : 0;
      tick.style.top = `${p * travel + thumbH / 2}px`;
    });

    this.update();
  }

  update() {
    const { maxScroll, travel } = this.metrics();
    const progress = maxScroll
      ? gsap.utils.clamp(0, 1, window.scrollY / maxScroll)
      : 0;
    this.setY(progress * travel);

    if (this.dragging) {
      this.percent.textContent = `${String(Math.round(progress * 100)).padStart(3, '0')}%`;
    }

    // Section active : la dernière dont le haut est passé à 40 % de l'écran
    const marker = window.scrollY + window.innerHeight * 0.4;
    let current = -1;
    this.tops.forEach((top, i) => {
      if (top <= marker) current = i;
    });
    if (maxScroll && window.scrollY >= maxScroll - 2)
      current = this.tops.length - 1;

    if (current !== this.current) {
      this.ticks[this.current]?.classList.remove('is-current');
      this.ticks[current]?.classList.add('is-current');
      this.current = current;
    }
  }

  goTo(i) {
    window.scrollTo({ top: this.tops[i], behavior: 'smooth' });
  }

  /* ---------- Scroll ---------- */

  onScroll() {
    this.update();

    this.element.classList.add('is-active');
    this.hideCall?.kill();
    this.hideCall = gsap.delayedCall(this.options.hideDelay, () =>
      this.element.classList.remove('is-active'),
    );

    // Séparation RVB proportionnelle à la vitesse, qui retombe ensuite
    if (!this.reduceMotion) {
      const dy = window.scrollY - this.lastScroll;
      const { splitStrength, maxSplit } = this.options;
      const v = gsap.utils.clamp(-maxSplit, maxSplit, dy * splitStrength);

      gsap.to(this.state, {
        split: v,
        duration: 0.08,
        overwrite: true,
        onUpdate: this.render,
      });
      this.settleCall?.kill();
      this.settleCall = gsap.delayedCall(0.08, () =>
        gsap.to(this.state, {
          split: 0,
          duration: 0.6,
          ease: 'power3.out',
          overwrite: true,
          onUpdate: this.render,
        }),
      );
    }
    this.lastScroll = window.scrollY;
  }

  render() {
    this.element.style.setProperty(
      '--split',
      `${this.state.split.toFixed(2)}px`,
    );
  }

  /* ---------- Drag du curseur et clic sur le rail ---------- */

  onThumbDown(e) {
    e.preventDefault();
    e.stopPropagation();
    this.dragging = true;
    this.dragStartY = e.clientY;
    this.dragStartScroll = window.scrollY;
    this.element.classList.add('is-dragging');
    this.update();

    window.addEventListener('pointermove', this.onDragMove);
    window.addEventListener('pointerup', this.onDragEnd);
    window.addEventListener('pointercancel', this.onDragEnd);
  }

  onDragMove(e) {
    const { maxScroll, travel } = this.metrics();
    if (!travel) return;
    const dy = e.clientY - this.dragStartY;
    window.scrollTo({
      top: this.dragStartScroll + dy * (maxScroll / travel),
      behavior: 'instant',
    });
  }

  onDragEnd() {
    this.dragging = false;
    this.element.classList.remove('is-dragging');
    window.removeEventListener('pointermove', this.onDragMove);
    window.removeEventListener('pointerup', this.onDragEnd);
    window.removeEventListener('pointercancel', this.onDragEnd);
  }

  onTrackDown(e) {
    const { maxScroll, thumbH, travel } = this.metrics();
    const rect = this.track.getBoundingClientRect();
    const y = gsap.utils.clamp(0, travel, e.clientY - rect.top - thumbH / 2);
    window.scrollTo({ top: (y / travel) * maxScroll, behavior: 'smooth' });
  }

  destroy() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onLayout);
    this.ro?.disconnect();
    this.onDragEnd();
    this.hideCall?.kill();
    this.settleCall?.kill();
    gsap.killTweensOf([this.thumb, this.state]);
    this.element?.remove();
    document.documentElement.classList.remove('has-scrollrail');
  }
}

const scrollRail = new ScrollRail();
